import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Sun,
  Moon,
  Store,
  Maximize,
  Minimize,
  ShoppingCart,
  Clipboard,
  LogOut,
  User,
} from "lucide-react";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AsyncReactSelect } from "@/components/ui/react-select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MobileSidebar } from "./Sidebar";
import { useSelector, useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import apiClient from "../../api/apiClient";
import { all_routes } from "../../Router/all_routes";
import { setcurrent_store } from "../../core/redux/action";

// Modals
import { DailyTasksModal } from "../modals/DailyTasksModal";

export function Header({ onLogout }) {
  const { theme, setTheme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const token = useSelector((state) => state.bearer_token);
  const currentStore = useSelector((state) => state.current_store);
  const userData = useSelector((state) => state.user_data) || {};
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (Array.isArray(permission)) {
      return permission.some((p) => permissions.includes(p));
    }
    return permissions.includes(permission);
  };

  const [isFullscreen, setIsFullscreen] = useState(false);

  // Modals state
  const [showDailyTasksModal, setShowDailyTasksModal] = useState(false);
  // Data fetching
  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get("/store");
      return res.data.data || [];
    },
    enabled: !!token,
  });

  const { data: onboardingStatus } = useQuery({
    queryKey: ["onboarding-status"],
    queryFn: async () => {
      const res = await apiClient.get("/init-page/onboarding");
      return res.data.data;
    },
    enabled: !!token,
  });

  const { data: dailyTasks = [] } = useQuery({
    queryKey: ["dailyTasks"],
    queryFn: async () => {
      const res = await apiClient.get("/tasks/daily");
      return res.data.data || [];
    },
    enabled: !!token && hasPermission("task daily read"),
  });

  useEffect(() => {
    // Automatically select the first store if redux (currentStore) isn't set and stores are available
    if (stores.length > 0 && !currentStore?.id) {
      dispatch(setcurrent_store(stores[0]));
    }
  }, [stores, currentStore, dispatch]);

  useEffect(() => {
    if (dailyTasks.length > 0 && userData?.id) {
      const today = new Date().toISOString().split("T")[0];
      const modalKey = `dailyTasksShown_${userData.id}_${today}`;
      if (!localStorage.getItem(modalKey)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setShowDailyTasksModal(true);
        localStorage.setItem(modalKey, "true");
      }
    }
  }, [dailyTasks, userData?.id]);

  useEffect(() => {
    if (onboardingStatus) {
      const isMandatoryPending =
        !onboardingStatus.is_email_changed ||
        !onboardingStatus.is_password_updated ||
        !onboardingStatus.is_store_setup_completed;

      if (isMandatoryPending && location.pathname !== all_routes.onboarding) {
        navigate(all_routes.onboarding);
      }
    }
  }, [onboardingStatus, navigate, location.pathname]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleKeyDown = (e) => {
      if (e.key === "F11") {
        e.preventDefault();
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch((err) => {
            console.error(
              `Error attempting to enable fullscreen: ${err.message}`,
            );
          });
        } else {
          document.exitFullscreen();
        }
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Handlers
  const formatStoreToOption = (s) => ({
    value: s.id,
    label: s.name,
    logo: s.logo,
    store: s,
  });

  const loadStoreOptions = async (inputValue) => {
    try {
      const res = await apiClient.get("/store", {
        params: { search: inputValue, perPage: 20 },
      });
      const data = res.data?.data ?? [];
      return data.map(formatStoreToOption);
    } catch {
      return [];
    }
  };

  const handleStoreChange = (selectedOption) => {
    if (!selectedOption) return;
    const storeId = selectedOption.value;
    const selected =
      selectedOption.store || stores.find((s) => String(s.id) === String(storeId));
    if (selected) {
      dispatch(setcurrent_store(selected));
      // Invalidate all queries to refresh data for the new store
      queryClient.invalidateQueries();
      toast.success(`Switched to ${selected.name}`);
    }
  };

  const currentStoreOption = useMemo(
    () => (currentStore?.id ? formatStoreToOption(currentStore) : null),
    [currentStore],
  );

  const defaultStoreOptions = useMemo(
    () => stores.map(formatStoreToOption),
    [stores],
  );

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const fullName = userData.employee?.full_name || userData.username || "User";
  const userEmail =
    userData.employee?.email || userData.email || "No email on file";

  return (
    <header className="sticky top-0 z-50 flex h-16 items-center gap-2 sm:gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-2 sm:px-4 md:px-6 shadow-sm w-full min-w-0 overflow-x-hidden">
      <div className="flex items-center gap-1 sm:gap-2 xl:hidden shrink-0">
        <MobileSidebar onLogout={onLogout} />
      </div>

      <div className="flex-1 flex items-center gap-2 sm:gap-4 min-w-0">
        <div className="w-full max-w-[240px] sm:max-w-[280px] md:max-w-[320px]">
          <AsyncReactSelect
            id="header-store-select"
            loadOptions={loadStoreOptions}
            defaultOptions={defaultStoreOptions}
            value={currentStoreOption}
            onChange={handleStoreChange}
            placeholder="Select store…"
            isClearable={false}
            formatOptionLabel={(option, { context }) => {
              const isSelected =
                String(option.value) === String(currentStore?.id);
              const isMenuSelected = context === "menu" && isSelected;

              return (
                <div className="flex items-center gap-2 min-w-0">
                  {option.logo ? (
                    <div className="h-4.5 w-4.5 rounded-xs overflow-hidden bg-white flex items-center justify-center shrink-0 p-0.5 shadow-2xs">
                      <img
                        src={option.logo}
                        alt={option.label}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  ) : (
                    <Store
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isMenuSelected ? "text-white" : "text-primary",
                      )}
                    />
                  )}
                  <span className="truncate font-medium">{option.label}</span>
                </div>
              );
            }}
            classNames={{
              control: () =>
                "min-h-[36px] h-9 py-0 bg-background dark:bg-muted/40 border-border hover:bg-muted/30 transition-colors",
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-0.5 sm:gap-1.5 shrink-0">
        {hasPermission(["cart read", "cart create"]) && (
          <Button
            variant="ghost"
            size="icon"
            asChild
            className="text-muted-foreground hover:text-primary h-8 w-8 sm:h-9 sm:w-9"
          >
            <Link to={all_routes.pos} title="Go to POS">
              <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
            </Link>
          </Button>
        )}

        {hasPermission("task daily read") && (
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-primary relative h-8 w-8 sm:h-9 sm:w-9"
            onClick={() => setShowDailyTasksModal(true)}
            title="Daily Tasks"
          >
            <Clipboard className="h-4 w-4 sm:h-5 sm:w-5" />
            {dailyTasks.length > 0 && (
              <span className="absolute top-1 sm:top-1.5 right-1 sm:right-1.5 flex h-3 w-3 sm:h-4 sm:w-4 items-center justify-center rounded-full bg-destructive text-[9px] sm:text-[10px] font-bold text-destructive-foreground">
                {dailyTasks.length}
              </span>
            )}
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-primary hidden sm:flex"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? (
            <Minimize className="h-5 w-5" />
          ) : (
            <Maximize className="h-5 w-5" />
          )}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-primary h-8 w-8 sm:h-9 sm:w-9"
          onClick={() => setTheme(theme === "light" ? "dark" : "light")}
        >
          {theme === "dark" ? (
            <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
          ) : (
            <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
          )}
        </Button>

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-full ml-0.5 sm:ml-1 shrink-0"
            >
              <Avatar className="h-8 w-8 sm:h-9 sm:w-9 border border-border">
                <AvatarImage src={userData.image} alt={fullName} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  <User className="h-4 w-4 sm:h-5 sm:w-5" />
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 bg-[hsl(var(--popover))] text-[hsl(var(--popover-foreground))] border shadow-md z-[100]"
            align="end"
            forceMount
          >
            <DropdownMenuLabel className="font-normal p-4">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-semibold leading-none">{fullName}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {userEmail}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to={all_routes.dashboard}
                className="cursor-pointer w-full flex items-center"
              >
                <LayoutGrid className="mr-2 h-4 w-4" />
                <span>Dashboard</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
              onClick={onLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Modals */}
      <DailyTasksModal
        open={showDailyTasksModal}
        onOpenChange={setShowDailyTasksModal}
        tasks={dailyTasks}
        userName={fullName}
      />
    </header>
  );
}
